/**
 * RoleGate — wraps the entire admin portal.
 *
 * Admits anyone with an admin right in ANY platform:
 *   - Retail: one of the ROP roles below (resolved by pcb-retail-api from the bearer), or
 *     isAdmin on v3/retail/admin/capabilities;
 *   - another platform: isAdmin on its own v3/{platform}/admin/capabilities (e.g. the Hub's
 *     hub.admin.upns), which needs the shared backend token.
 * Finer gating happens inside (platform switcher, six sections, per-page roles), and every API
 * still checks every call itself (docs/architecture/admin-portal-shape.md §5).
 *
 * Retail roles (any one is sufficient): SuperAdmin, Admin (legacy umbrella), RetailAdmin,
 * SysOpsManager, RiskManager, DisclosureManager.
 *
 * No bypass-key escape — admin.auth.allowBypassKey was flipped to false
 * 2026-05-05 (memory project_admin_auth_allowbypasskey_off_20260514).
 */

import { type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { RETAIL_ADMIN_ROLES, usePlatforms } from '@/contexts/PlatformsContext';
import { AccessDenied } from '@/modules/AccessDenied/AccessDenied';

export function RoleGate({ children }: { children: ReactNode }): JSX.Element {
  const { user, status, loading, error, refresh } = useAuth();
  const platforms = usePlatforms();

  if (loading) {
    return <div className="loading-screen">Signing in to the admin portal…</div>;
  }

  if (status === 'error') {
    return (
      <div className="auth-gate">
        <div className="auth-gate__card">
          <h1 className="auth-gate__title">Sign-in failed</h1>
          <p className="auth-gate__body">
            {error ?? 'Unable to complete Entra sign-in.'}
          </p>
          <button className="btn btn--primary" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <div className="loading-screen">Resolving identity…</div>;
  }

  const hasRetailRole = user.roles.some((r) =>
    (RETAIL_ADMIN_ROLES as readonly string[]).includes(r),
  );

  if (hasRetailRole || platforms.available.length > 0) {
    return <>{children}</>;
  }

  if (platforms.loading) {
    return <div className="loading-screen">Checking your admin rights…</div>;
  }

  return <AccessDenied user={user} />;
}
